import React, {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import { Button, theme} from 'antd';
import {userActions} from '../../state/actions/user.actions';
import {notificationTypesConstants} from '../../constants';
import {
  StyledNotifications,
  StyledNotificationEmpty,
  StyledNotificationsList
} from './StyledNotifications';
import Notification from './Notification';
import defaultAvatarLogo from '../../static/images/avatar-withless-logo.svg?url';
import routes from '../../routes/routes.json';
import {objectHelpers} from '../../utils/helpers';

const { useToken } = theme;

const getParamId = (params) => {
  const list = params.split('=');
  return list[list.length - 1];
}

const formatNotificationData = (notification = {}) => {
  const {
    message = '',
    type = '',
    timestamp = null,
    id = null,
    is_read: isRead = false,
    action_url_params: actionParams = ''
  } = notification;

  let actionLink = '';

  switch (type) {
    case notificationTypesConstants.ADD_USERS:
      actionLink = `${routes.team}?add-user=1`;
      break;
    case notificationTypesConstants.MANAGE_EXPENSES:
    case notificationTypesConstants.EXPENSE_REJECTED:
    case notificationTypesConstants.EXPENSE_PAID:
    case notificationTypesConstants.EXPENSE_SCHEDULED_PAID:
      actionLink = `${routes.expensesList}${actionParams}`;
      break;
    case notificationTypesConstants.EXPENSE_NEW_NOTE:
      // The backend sends `selectedExpenseId=<id>&tab=activity`, without a leading `?`.
      actionLink = `${routes.expensesList}?${actionParams}`;
      break;
    case notificationTypesConstants.MANAGER_REPORTS:
      actionLink = `${routes.reportsList}${actionParams}`;
      break;
    case notificationTypesConstants.REPORT_CLOSED:
      actionLink = `${routes.reportsList}/${actionParams}`;
      break;
    case notificationTypesConstants.SUBSCRIPTION_CARD_CREATE:
    case notificationTypesConstants.SUBSCRIPTION_CARD_BLOCKED:
    case notificationTypesConstants.SUBSCRIPTION_CARD_UNBLOCKED:
      actionLink = `${routes.subscriptionsList}/${getParamId(actionParams)}`;
      break;
    default:
      actionLink = '';
  }

  return {
    actionLink,
    avatar: { src: defaultAvatarLogo },
    id,
    isRead,
    message,
    timestamp,
    type
  };
}

const Notifications = ({
  ...rest
}) => {
  const dispatch = useDispatch();
  const notifications = useSelector(state => state.user.notifications);
  const [t] = useTranslation('main');
  const [data, setData] = useState([]);
  const [isEnabledLoadMore, setIsEnabledLoadMore] = useState(false);
  const navigate = useNavigate();
  const notificationListRef = useRef(null);

  const { token } = useToken();

  const contentStyle = {
    backgroundColor: token.colorBgElevated,
    boxShadow: token.boxShadowSecondary,
  };

  useEffect(() => {
    const { notifications: notificationsList = [], pagination = {} } = notifications || {};
    const hasNextPage = Boolean(objectHelpers.getObjProp(pagination, 'next_page')) || false;
    const data = Array.isArray(notificationsList)
      ? notificationsList
        .map(formatNotificationData)
        .sort((x, y) => y?.timestamp - x?.timestamp)
      : [];
    setData(data);
    setIsEnabledLoadMore(hasNextPage);
  }, [notifications]);

  const handleReadAll = () => dispatch(userActions.readAllNotifications());

  const handleOnNotificationClick = ({id, isRead, actionLink}) => {
    if (id && !isRead) {
      dispatch(userActions.readNotifications({
        ids: [id], successCallback: () => dispatch(userActions.getNotificationsListWithCount())
      }));
    }
    if (actionLink) {
      navigate(actionLink);
    }
  }

  const handleOnScroll = () => {
    if (notificationListRef.current && isEnabledLoadMore) {
      const { scrollTop, scrollHeight, clientHeight } = notificationListRef.current;
      if (scrollTop + clientHeight === scrollHeight) dispatch(userActions.getNotificationsListWithCount(true));
    }
  }

  return (
    <StyledNotifications
      style={contentStyle}
      {...rest}
    >
      <header className='d-flex align-items-center justify-space-between'>
        {t('notifications')}
        <Button
          onClick={handleReadAll}
          type='text'
        >
          {t('markAllAsRead')}
        </Button>
      </header>
      <StyledNotificationsList
        onScroll={handleOnScroll}
        ref={notificationListRef}
      >
        {data.length === 0 && (
          <StyledNotificationEmpty
            description={false}
          />
        )}
        {data.map((item, i) => (
          <Notification
            {...item}
            data-test='notification'
            key={i}
            onClick={() => handleOnNotificationClick(item)}
          />
        ))}
      </StyledNotificationsList>
    </StyledNotifications>
  );
}

export default Notifications;
